/**
 * Budsjett — budget against actual, per year.  Money-pro G1 (owner, meeting notes 2026-10-31: «regnskap = NOT bookkeeping,
 * just budget vs actual»).
 *
 * A mosque writes its year's budget as lines: income or cost, a category in its own words (≤ 60; suggestions below), an
 * amount in øre and a short note. Beside each line the panel shows what actually came in or went out that year, from what
 * the platform already holds — never typed in twice:
 *
 *   income  Gaver              Vipps gifts captured (net of refunds), not for a campaign; confirmed cash gifts, not for a campaign
 *           Innsamlinger       Vipps gifts and confirmed cash gifts for a campaign
 *           Butikk             shop orders paid (paid, fulfilled, partly refunded): captured with Vipps net of refunds, or the total of an order paid otherwise
 *           Medlemskontingent  nothing yet — the platform takes no membership fee; the line is budget only (0 actual)
 *   cost    the expense claim's category as its Norwegian name (Mat og drikke, Utstyr, Reise, Arrangementer, Vedlikehold,
 *           Annet), approved or paid, by the day the money was spent
 *
 * A category is matched to a line by name, ignoring case and spaces. An actual with no line of its name is still shown
 * («ikke budsjettert»), so nothing that came in or went out is hidden. Difference = actual − budget (for income more is
 * good, for cost less is).
 *
 * Not bookkeeping: no accounts, no VAT, no vouchers. The CSV is for the accountant to read beside the books.
 *
 * Pure: no I/O, no clock. Days are mosque-local wall dates, made by the caller (tz.dateIn).
 */
'use strict';
const Csv = require('./csv');
const { formatKr } = require('./money');
const { oneLine, text, isId } = require('./fields');

const KINDS = ['income', 'cost'];
const KIND_NO = { income: 'Inntekter', cost: 'Kostnader' };
const MAX_CATEGORY = 60;
const MAX_NOTE = 200;
/** 100 million kroner: a budget line above that is a typo. */
const MAX_AMOUNT_ORE = 100_000_000_00;
const MIN_YEAR = 2000;
const MAX_YEAR = 2100;
const MAX_LINES = 200;
const SUGGESTIONS = {
  income: ['Gaver', 'Medlemskontingent', 'Innsamlinger', 'Butikk', 'Leieinntekter', 'Annet'],
  cost: ['Leie', 'Strøm', 'Lønn/honorar', 'Vedlikehold', 'Arrangementer', 'Mat og drikke', 'Utstyr', 'Reise', 'Annet'],
};
/** Where the platform's own figures land (income). */
const INCOME = { gifts: 'Gaver', membership: 'Medlemskontingent', campaign: 'Innsamlinger', store: 'Butikk' };
/** An expense claim's category → the cost category it counts for. */
const EXPENSE_CATEGORY = { food: 'Mat og drikke', equipment: 'Utstyr', travel: 'Reise', event: 'Arrangementer', maintenance: 'Vedlikehold', other: 'Annet' };

const key = (s) => String(s || '').toLowerCase().replace(/\s+/g, '');
const validYear = (y) => Number.isSafeInteger(y) && y >= MIN_YEAR && y <= MAX_YEAR;
/** The year from a query (default: the year of `today`). → { ok, value } | { ok: false, message } */
function yearOf(v, today) {
  const y = v === undefined || v === null || v === '' ? Number(String(today || '').slice(0, 4)) : (/^\d{4}$/.test(String(v)) ? Number(v) : NaN);
  return validYear(y) ? { ok: true, value: y } : { ok: false, message: `Velg et år mellom ${MIN_YEAR} og ${MAX_YEAR}.` };
}

/**
 * A budget line as staff write it. A new line needs year, kind, category and amount; a change (`base` = the line as it is)
 * may send any of category, amount and note (year and kind stay). Unknown fields refused.
 * → { ok, value: { year, kind, category, amountOre, note }, changed: [field…] } | { ok: false, errors }
 */
function lineInput(input, { base = null } = {}) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { line: 'Ugyldig budsjettlinje.' } };
  const allowed = base ? ['category', 'amountOre', 'note'] : ['year', 'kind', 'category', 'amountOre', 'note'];
  const unknown = Object.keys(src).filter((k) => !allowed.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, base && (k === 'year' || k === 'kind') ? 'Kan ikke endres.' : 'Ukjent felt.'])) };
  const has = (k) => Object.prototype.hasOwnProperty.call(src, k);
  const cur = base || {};
  const errors = {}, out = {};
  out.year = base ? cur.year : src.year;
  if (!validYear(out.year)) errors.year = `Velg et år mellom ${MIN_YEAR} og ${MAX_YEAR}.`;
  out.kind = base ? cur.kind : src.kind;
  if (!KINDS.includes(out.kind)) errors.kind = 'Velg inntekt eller kostnad.';
  const cat = oneLine(has('category') ? src.category : cur.category, 'en kategori', MAX_CATEGORY);
  if (cat.ok) out.category = cat.value; else errors.category = cat.message;
  const amt = has('amountOre') ? src.amountOre : cur.amountOre;
  if (!Number.isSafeInteger(amt) || amt < 0 || amt > MAX_AMOUNT_ORE) errors.amountOre = 'Beløpet må være et helt antall øre, 0 eller mer.';
  else out.amountOre = amt;
  const note = text(has('note') ? src.note : (cur.note ?? null), 'merknaden', MAX_NOTE);
  if (note.ok) out.note = note.value; else errors.note = note.message;
  if (Object.keys(errors).length) return { ok: false, errors };
  const changed = base ? ['category', 'amountOre', 'note'].filter((k) => (base[k] ?? null) !== (out[k] ?? null)) : ['year', 'kind', 'category', 'amountOre', 'note'];
  return { ok: true, value: out, changed };
}
/** A budget amount as typed, in kroner: «12 000», «12000,50». Whole øre, 0 or more, up to the line limit. → { ok, ore } | { ok: false, message } */
function kroner(input) {
  const s = String(input ?? '').trim().replace(/[\s\u00a0]/g, '').replace(/kr$/i, '');
  const m = /^(\d{1,9})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return { ok: false, message: 'Skriv beløpet i kroner, for eksempel 12000 eller 12000,50.' };
  const ore = Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0') || 0);
  return ore <= MAX_AMOUNT_ORE ? { ok: true, ore } : { ok: false, message: 'Beløpet er for stort.' };
}
/** The same category twice in one year and kind is one line too many (names compared without case and spaces). */
const sameLine = (a, b) => a.year === b.year && a.kind === b.kind && key(a.category) === key(b.category);

/**
 * «Kopier fra i fjor»: last year's lines as new lines for `year`, except a category this year already has.
 * → [{ year, kind, category, amountOre, note }]
 */
function copyPlan(previous, current, year) {
  const have = new Set((current || []).map((l) => `${l.kind}:${key(l.category)}`));
  return (previous || []).filter((l) => KINDS.includes(l.kind) && !have.has(`${l.kind}:${key(l.category)}`))
    .map((l) => ({ year, kind: l.kind, category: l.category, amountOre: l.amountOre, note: l.note ?? null }));
}

const inYear = (day, year) => typeof day === 'string' && day.slice(0, 4) === String(year);
const add = (m, cat, ore) => { if (Number.isSafeInteger(ore) && ore !== 0) m.set(cat, (m.get(cat) || 0) + ore); };
/**
 * What actually came in and went out in `year`, per category (øre). The caller hands in the platform's own records, each
 * with its mosque-local day:
 *   gifts    [{ day, ore, campaign: bool }]   captured Vipps gifts, net of refunds (campaigns.giftOre)
 *   cash     [{ day, ore, campaign: bool }]   confirmed cash gifts (a reversal is negative)
 *   store    [{ day, ore }]                   paid shop orders
 *   expenses [claim]                          expense claims as core reads them (approved and paid count, by spentOn)
 * → { income: Map(category → øre), cost: Map(category → øre) }
 */
function actuals({ gifts = [], cash = [], store = [], expenses = [] } = {}, year) {
  const income = new Map(), cost = new Map();
  for (const g of [...gifts, ...cash]) if (g && inYear(g.day, year)) add(income, g.campaign ? INCOME.campaign : INCOME.gifts, g.ore);
  for (const o of store) if (o && inYear(o.day, year)) add(income, INCOME.store, o.ore);
  for (const c of expenses) {
    if (c && (c.status === 'approved' || c.status === 'paid') && inYear(c.spentOn, year)) add(cost, EXPENSE_CATEGORY[c.category] || 'Annet', c.amountOre);
  }
  return { income, cost };
}

/**
 * Budget beside actual, one kind: every line of that kind (in the order written), then every actual category that has no
 * line («ikke budsjettert»). Difference = actual − budget.
 * → { rows: [{ id|null, category, budgetOre, actualOre, differenceOre, note, budgeted }], budgetOre, actualOre, differenceOre }
 */
function compare(lines, actualMap, kind) {
  const mine = (lines || []).filter((l) => l.kind === kind);
  const left = new Map([...(actualMap || new Map())].map(([cat, ore]) => [key(cat), { cat, ore }]));
  const rows = mine.map((l) => {
    const a = left.get(key(l.category));
    left.delete(key(l.category));
    const actualOre = a ? a.ore : 0;
    return { id: l.id || null, category: l.category, budgetOre: l.amountOre, actualOre, differenceOre: actualOre - l.amountOre, note: l.note ?? null, budgeted: true, updatedAt: l.updatedAt ?? null };
  });
  for (const { cat, ore } of left.values()) rows.push({ id: null, category: cat, budgetOre: 0, actualOre: ore, differenceOre: ore, note: null, budgeted: false, updatedAt: null });
  const budgetOre = rows.reduce((n, r) => n + r.budgetOre, 0), actualOre = rows.reduce((n, r) => n + r.actualOre, 0);
  return { rows, budgetOre, actualOre, differenceOre: actualOre - budgetOre };
}
/** The whole year: both kinds, and the result (income − cost) budgeted and actual. */
function overview(lines, act) {
  const income = compare(lines, act.income, 'income'), cost = compare(lines, act.cost, 'cost');
  return { income, cost, result: { budgetOre: income.budgetOre - cost.budgetOre, actualOre: income.actualOre - cost.actualOre } };
}

/** The accountant's file: Type;Kategori;Budsjett (kr);Faktisk (kr);Differanse (kr) — semicolons, CRLF, a formula start neutralised. */
const CSV_HEAD = ['Type', 'Kategori', 'Budsjett (kr)', 'Faktisk (kr)', 'Differanse (kr)', 'Merknad'];
function csv(view) {
  const n = (ore) => Csv.num(formatKr(ore, { grouped: false }));
  const lines = [];
  for (const kind of KINDS) {
    for (const r of view[kind].rows) lines.push(Csv.line([Csv.cell(KIND_NO[kind]), Csv.cell(r.category), n(r.budgetOre), n(r.actualOre), n(r.differenceOre), Csv.cell(r.note || (r.budgeted ? '' : 'ikke budsjettert'))]));
    lines.push(Csv.line([Csv.cell(KIND_NO[kind]), Csv.cell('Sum'), n(view[kind].budgetOre), n(view[kind].actualOre), n(view[kind].differenceOre), Csv.cell('')]));
  }
  lines.push(Csv.line([Csv.cell('Resultat'), Csv.cell('Inntekter − kostnader'), n(view.result.budgetOre), n(view.result.actualOre), n(view.result.actualOre - view.result.budgetOre), Csv.cell('')]));
  return Csv.table(CSV_HEAD, lines);
}

/** What the activity log keeps of a line: year, kind, category, amount. Operational: a budget is about the mosque, not a person. */
function auditView(l = {}) {
  const out = {};
  if (l.year !== undefined) out.budget_year = l.year;
  if (l.kind !== undefined) out.budget_kind = l.kind;
  if (l.category !== undefined) out.budget_category = l.category;
  if (l.amountOre !== undefined) out.budget_amount_ore = l.amountOre;
  return out;
}

module.exports = { KINDS, KIND_NO, MAX_CATEGORY, MAX_NOTE, MAX_AMOUNT_ORE, MIN_YEAR, MAX_YEAR, MAX_LINES, SUGGESTIONS, INCOME, EXPENSE_CATEGORY, CSV_HEAD,
  isId, validYear, yearOf, kroner, lineInput, sameLine, copyPlan, actuals, compare, overview, csv, auditView, categoryKey: key };
